/* ═══════════════════════════════════════════════════════════════════
   [D-448] Картка юзера партнера — за зразком картки юзера в ZODIAQ
   (вимога OWNER 05.10: «гарна карточка управління як у ZODIAQ»).

   Один компонент на дашборд OWNER і (далі) кабінет партнера: різниться лише
   джерелом даних (`load*`/`setFeat`), верстка спільна — щоб дві копії не
   розійшлися, як уже розходився рецепт унікалізації.
   ═══════════════════════════════════════════════════════════════════ */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button, Stack, TextField,
} from '@mui/material';
import FeatureRows, { FeatureLegend } from './FeatureRows';
import SimWhy, { simOrders, simCol } from './SimWhy';   // [D-701] «🔬 Схожість» - лише в дашборді OWNER (loadSim)

const C = {
  bg: '#101a24', line: 'rgba(0,229,160,.14)', txt: '#eaf6ff', dim: '#9fb4c6',
  neon: '#00e5a0', warn: '#ffb74d', bad: '#ff6b6b',
};

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('uk-UA') : '—');
const fmtDT = (v) => (v ? new Date(v).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

// дата, до якої діє доступ: підписка, інакше тріал
export const accessUntil = (u) => (u && (u.unlimited ? null : (u.subscription_end || u.trial_end))) || null;

function countdown(iso, now) {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - now;
  if (ms <= 0) return 'закінчився';
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return (d ? d + ' дн ' : '') + String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0');
}

/* [D-676] «💶 Історія фінансів» учня. Усі суми й підсумки - із сервера (calc.money_totals, calc.v5_history); тут лише показ. */
const CCY3 = { UAH: '₴', EUR: '€', USD: '$' };
const mfmt = (v, c) => Number(v).toLocaleString('uk-UA', { maximumFractionDigits: 2 }) + ' ' + (CCY3[c] || c || '');
const PAY_ST = { ok: [C.neon, 'зараховано'], wait: [C.warn, '⏳ чекає'], no: [C.bad, 'відхилено'] };
const moneySummary = (mh) => {
  if (!mh) return '…';
  if (mh.err) return '—';
  const t = Object.entries(mh.pay_totals || {}).map(([c, v]) => mfmt(v, c)).join(' · ') || (mh.pays.length ? '0' : 'немає');
  return t + (mh.has_tokens ? ' · 🎟 ' + (mh.tok_sum.balance || 0).toLocaleString('uk-UA') : '') + ' ›';
};
function MoneyHist({ mh }) {
  const [sub, setSub] = useState('pays');
  if (!mh) return <Typography sx={{ color: C.dim }}>Завантаження…</Typography>;
  if (mh.err) return <Typography sx={{ color: C.bad }}>Не вдалося завантажити історію.</Typography>;
  const ts = mh.tok_sum || {};
  const tile = (l, v, col) => (
    <Box key={l} sx={{ p: 1, borderRadius: 2, bgcolor: 'rgba(255,255,255,.03)' }}>
      <Typography sx={{ color: C.dim, fontSize: 11 }}>{l}</Typography><Typography sx={{ fontWeight: 800, fontSize: 15, color: col }}>{v}</Typography>
    </Box>
  );
  const line = (key, title, sub2, right, rightCol, right2, r2col) => (
    <Box key={key} sx={{ display: 'flex', gap: 1, alignItems: 'center', py: .8, borderBottom: '1px solid rgba(255,255,255,.06)' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.txt, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</Typography>
        <Typography sx={{ fontSize: 11.5, color: C.dim, lineHeight: 1.35 }}>{sub2}</Typography>
      </Box>
      <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <Typography sx={{ fontWeight: 800, color: rightCol, fontSize: 14 }}>{right}</Typography>
        <Typography sx={{ fontSize: 10.5, color: r2col || C.dim }}>{right2}</Typography>
      </Box>
    </Box>
  );
  const pill = (k, l) => (
    <Button key={k} size="small" onClick={() => setSub(k)} variant={sub === k ? 'contained' : 'outlined'}
      sx={{ textTransform: 'none', borderRadius: 5, px: 1.6, fontSize: 12.5, borderColor: 'rgba(0,229,160,.3)', color: sub === k ? '#04121a' : C.neon,
            bgcolor: sub === k ? C.neon : 'transparent', '&:hover': { bgcolor: sub === k ? C.neon : 'rgba(0,229,160,.08)' } }}>{l}</Button>
  );
  return (<>
    <Box sx={{ display: 'flex', gap: .8 }}>{pill('pays', '💳 Поповнення · ' + mh.pays.length)}{pill('tok', '🎟 Токени · ' + mh.tok.length)}</Box>
    {sub === 'pays' && (<>
      <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}>
        {tile('Сплачено (зараховано)', Object.entries(mh.pay_totals || {}).map(([c, v]) => mfmt(v, c)).join(' · ') || '0', C.neon)}
        {tile('Поповнень', mh.pay_ok + (mh.pay_wait ? ' · ⏳ ' + mh.pay_wait : ''), C.txt)}
      </Box>
      <Box>
        {mh.pays.length === 0 && <Typography sx={{ color: C.dim }}>Поповнень ще не було.</Typography>}
        {mh.pays.map((d, i) => { const [sc, sl] = PAY_ST[d.st] || PAY_ST.wait;
          return line('p' + i, d.what, fmtDT(d.at) + ' · ' + d.to + ' · ' + d.via, mfmt(d.amount, d.ccy), d.st === 'ok' ? C.txt : C.dim, sl, sc); })}
      </Box>
    </>)}
    {sub === 'tok' && (<>
      <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))' }}>
        {tile('Куплено', '+' + ((ts.bought || 0) + (ts.granted || 0)).toLocaleString('uk-UA'), C.neon)}
        {tile('Витрачено', '−' + ((ts.spent || 0) + (ts.taken || 0)).toLocaleString('uk-UA'), C.bad)}
        {tile('Повернуто', '+' + (ts.refunded || 0).toLocaleString('uk-UA'), '#4fc3f7')}
        {tile('Залишок', (ts.balance || 0).toLocaleString('uk-UA') + (ts.chain_ok ? ' ✓' : ' ⚠'), ts.chain_ok ? C.txt : C.bad)}
      </Box>
      <Typography sx={{ fontSize: 11.5, color: ts.chain_ok ? C.dim : C.bad }}>
        {ts.chain_ok ? '✓ Журнал сходиться: залишок = усі зміни разом (перевіряє калькулятор).' : '⚠ Журнал не сходиться - перевірити нічну звірку.'}</Typography>
      <Box>
        {mh.tok.length === 0 && <Typography sx={{ color: C.dim }}>Токенів ще не було.</Typography>}
        {mh.tok.map((d, i) => line('t' + i, d.lbl, (d.what ? d.what + ' · ' : '') + fmtDT(d.at),
          (d.delta > 0 ? '+' : '−') + Math.abs(d.delta).toLocaleString('uk-UA') + ' 🎟', d.delta > 0 ? C.neon : C.bad, '→ ' + d.after.toLocaleString('uk-UA')))}
      </Box>
    </>)}
  </>);
}

/* [D-701] OWNER 22.10: «бачив такі самі рекомендації проглядаючи інформацію у юзерів партнера, тільки так щоб партнер і його юзери не знали про це».
   Останні замовлення учня: схожість кожної версії; червоні (≥ 50 %) - з поясненням і варіантами «що зробить зеленим». */
function SimTab({ rows }) {
  if (rows === null) return <Typography sx={{ color: C.dim, fontSize: 13 }}>Завантаження…</Typography>;
  const ord = simOrders(rows, 10);
  if (!ord.length) return <Typography sx={{ color: C.dim, fontSize: 13 }}>Замірів схожості ще немає - вони з'являються після обробок.</Typography>;
  const red = ord.filter(o => o.max >= 50).length;
  return (
    <Box data-simtab="1">
      <Typography sx={{ color: C.dim, fontSize: 12, mb: 1 }}>
        🔬 Схожість готових версій з оригіналом (менше - краще: до 20 % зелене, до 50 % жовте). Останні {ord.length} замовлень, червоних {red}. Бачите лише ви.
      </Typography>
      {ord.map((o, i) => (
        <Box key={i} sx={{ mb: 1, p: 1, borderRadius: 2, bgcolor: 'rgba(255,255,255,.03)', border: '1px solid ' + (o.max >= 50 ? 'rgba(255,107,107,.35)' : C.line) }}>
          <Typography sx={{ fontSize: 13, color: C.txt, mb: o.max >= 50 ? .6 : 0 }}>
            {new Date(o.at).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })} ·{' '}
            {Object.keys(o.sims).sort((a, b) => a - b).map(v => (
              <span key={v} style={{ color: simCol(o.sims[v].same_pct), fontWeight: 700, marginRight: 8 }}>v{v} {Math.round(o.sims[v].same_pct)}%{o.sims[v].strong ? ' 💪' : ''}</span>))}
          </Typography>
          {o.max >= 50 && <SimWhy sims={o.sims} />}
        </Box>
      ))}
    </Box>
  );
}

export default function PartnerUserCard({ open, userId, loadUser, loadFeats, setFeat, setLimit, onClose, onOpenFull, onUnbind, onDelete, loadPrices, setPrice, loadMoney, loadSim }) {
  const [sim, setSim] = useState(null);   // [D-701] заміри схожості учня (лише коли сторінка «Партнери» дашборда OWNER дала loadSim)
  const [mh, setMh] = useState(null);   // [D-676] історія фінансів учня
  const [prices, setPrices] = useState(null);   // [D-619] своя ціна пакета для учня
  const [pBusy, setPBusy] = useState(false);
  // [D-589] OWNER 14.10: «у партнера… бачу його юзерів, я хочу щоб віконце (🎬 Відео на день) було активне і там я мав змінювати цей показник»
  const [lim, setLim] = useState(null);      // null - не редагуємо; рядок - поле вводу
  const [limBusy, setLimBusy] = useState(false);
  const saveLim = () => {
    const n = parseInt(lim, 10);
    if (!(n >= 1 && n <= 9999)) { setErr('Відео на день - від 1 до 9999'); return; }
    setLimBusy(true);
    setLimit(userId, n)
      .then(() => { setU(x => ({ ...x, daily_limit: n })); setLim(null); setErr(''); })
      .catch(e => setErr('Не вдалося змінити ліміт' + ((e.response && e.response.data && e.response.data.detail) ? ': ' + e.response.data.detail : '')))
      .finally(() => setLimBusy(false));
  };
  const [u, setU] = useState(null);
  const [feats, setFeats] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [now, setNow] = useState(Date.now());
  const [view, setView] = useState('profile');   // [D-459] профіль і функції — окремими розділами

  useEffect(() => {
    if (!open || !userId) return undefined;
    setU(null); setFeats(null); setErr('');
    loadUser(userId).then(setU).catch(e => setErr((e.response && e.response.data && e.response.data.detail) || 'Не вдалося завантажити юзера'));
    loadFeats(userId).then(setFeats).catch(() => setFeats([]));
    setPrices(null);
    if (loadPrices) loadPrices(userId).then(setPrices).catch(() => setPrices([]));   // [D-619]
    setMh(null);
    if (loadMoney) loadMoney(userId).then(setMh).catch(() => setMh({ err: 1 }));   // [D-676]
    setSim(null);
    if (loadSim) loadSim(userId).then(setSim).catch(() => setSim([]));   // [D-701]
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [open, userId]);  // loadUser/loadFeats — стабільні функції батька

  const onSet = (key, state) => {
    setBusy(key);
    setFeat(userId, key, state)
      .then(rows => { setFeats(rows); setErr(''); })
      .catch(e => setErr('Не вдалося змінити' + ((e.response && e.response.data && e.response.data.detail) ? ': ' + e.response.data.detail : '')))
      .finally(() => setBusy(''));
  };

  const until = accessUntil(u);
  const active = u && (u.unlimited || (until && new Date(until).getTime() > now));
  const rows = u ? [
    ['🆔 Telegram ID', u.telegram_id],
    ['🌐 Мова', u.lang_ui || '—'],
    ['📅 Прийшов', fmtDT(u.created_at)],
    ['🏷 Статус', u.status || '—'],
    ['📦 Пакет', u.package_name || '—'],
    ['🎬 Відео на день', u.unlimited ? '∞' : (u.daily_limit != null ? u.daily_limit : '—')],
    ['💶 Історія фінансів', moneySummary(mh)],   // [D-676] замість «📤 Публікація» - активне віконце
    ['🎞 Сьогодні відео', u.videos_today != null ? u.videos_today : '—'],
    ['📊 Всього відео / версій', (u.total_videos || 0) + ' / ' + (u.total_versions || 0)],
    ['🕐 Остання обробка', fmtDT(u.last_video_at)],
  ] : [];

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="md" fullWidth
            PaperProps={{ sx: { bgcolor: C.bg, backgroundImage: 'none', border: '1px solid ' + C.line } }}>
      <DialogTitle sx={{ color: C.txt, fontWeight: 700 }}>
        {u ? (u.telegram_username ? '@' + u.telegram_username : 'ID ' + u.telegram_id) : 'Юзер'}
      </DialogTitle>
      <DialogContent>
        {err && <Typography sx={{ color: C.bad, fontSize: 13, mb: 1 }}>{err}</Typography>}
        {!u && !err && <Typography sx={{ color: C.dim }}>Завантаження…</Typography>}
        {u && (
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', gap: .8, mb: .5, flexWrap: 'wrap' }}>{/* [D-728] на телефоні «🔬 Схожість» вилазила за край */}
              {[['profile', '👤 Профіль'], loadMoney && ['money', '💶 Фінанси'], ['feats', '🧩 Функції'], loadPrices && ['price', '💶 Ціна'], loadSim && ['sim', '🔬 Схожість']].filter(Boolean).map(([k, l]) => (
                <Button key={k} size="small" onClick={() => setView(k)} variant={view === k ? 'contained' : 'outlined'}
                  sx={{ textTransform: 'none', borderRadius: 5, px: 2, borderColor: 'rgba(0,229,160,.3)',
                        color: view === k ? '#04121a' : C.neon, bgcolor: view === k ? C.neon : 'transparent',
                        '&:hover': { bgcolor: view === k ? C.neon : 'rgba(0,229,160,.08)' } }}>{l}</Button>
              ))}
            </Box>
            {/* [D-728] OWNER 25.10: «щоб так само міг у любий час переглянути інформацію роботи партнера як учень» - кнопка була
                дрібним текстом унизу поруч із «Видалити учня»; тепер угорі під вкладками: та сама сторінка, що в «Користувачах» */}
            {onOpenFull && (
              <Button data-open-full="1" fullWidth onClick={() => onOpenFull(userId)}
                sx={{ textTransform: 'none', fontWeight: 700, fontSize: 14, justifyContent: 'flex-start', px: 1.6, py: 1, borderRadius: 2,
                      color: '#04121a', bgcolor: C.neon, '&:hover': { bgcolor: '#00c98c' } }}>
                🎬 Обробки й уся інформація - як у «Користувачах» →
              </Button>
            )}
            {view === 'profile' && (<>
            <Box sx={{ p: 1.5, borderRadius: 2,
                       bgcolor: active ? 'rgba(0,229,160,.08)' : 'rgba(255,107,107,.07)',
                       border: '1px solid ' + (active ? 'rgba(0,229,160,.3)' : 'rgba(255,107,107,.3)') }}>
              {u.unlimited ? (
                <Typography sx={{ color: C.neon, fontWeight: 700 }}>♾ Безлімітний доступ</Typography>
              ) : active ? (<>
                <Typography sx={{ color: C.neon, fontWeight: 700, fontSize: 14 }}>✅ Доступ активний</Typography>
                <Typography sx={{ color: C.dim, fontSize: 13 }}>діє до {fmtDate(until)}</Typography>
                <Typography sx={{ color: C.neon, fontFamily: 'monospace', fontSize: 18, fontWeight: 800 }}>
                  {countdown(until, now)}
                </Typography>
              </>) : (
                <Typography sx={{ color: C.bad, fontWeight: 700, fontSize: 14 }}>
                  ⛔ Доступу немає{until ? ' · закінчився ' + fmtDate(until) : ''}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))' }}>
              {rows.map(([k, v]) => (k === '🎬 Відео на день' && setLimit && !u.unlimited ? (
                /* [D-589] активне віконце: натиснути - змінити, Enter або 💾 - зберегти (юзер отримає повідомлення від бота, як зі сторінки «Юзери») */
                <Box key={k} data-limit-tile="1" onClick={() => lim === null && setLim(String(u.daily_limit != null ? u.daily_limit : ''))}
                     sx={{ p: 1.1, borderRadius: 2, bgcolor: 'rgba(0,229,160,.06)', border: '1px dashed rgba(0,229,160,.4)', cursor: lim === null ? 'pointer' : 'default' }}>
                  <Typography sx={{ color: C.dim, fontSize: 11.5 }}>{k} {lim === null ? '✏️' : ''}</Typography>
                  {lim === null ? (
                    <Typography sx={{ color: C.neon, fontSize: 14, fontWeight: 700 }}>{String(v)}</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', gap: .6, alignItems: 'center', mt: .3 }}>
                      <TextField size="small" type="number" autoFocus value={lim} disabled={limBusy}
                                 onChange={e => setLim(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') saveLim(); if (e.key === 'Escape') setLim(null); }}
                                 inputProps={{ min: 1, max: 9999, 'data-limit-input': '1', style: { color: C.txt, padding: '4px 8px', width: 64 } }} />
                      <Button size="small" data-limit-save="1" onClick={(e) => { e.stopPropagation(); saveLim(); }} disabled={limBusy}
                              sx={{ minWidth: 0, px: 1, color: C.neon }}>💾</Button>
                      <Button size="small" onClick={(e) => { e.stopPropagation(); setLim(null); }} disabled={limBusy} sx={{ minWidth: 0, px: 1, color: C.dim }}>✕</Button>
                    </Box>
                  )}
                </Box>
              ) : k.startsWith('💶') && loadMoney ? (
                /* [D-676] активне віконце: натиснути - «💶 Фінанси» (поповнення й токени) */
                <Box key={k} data-money-tile="1" onClick={() => setView('money')}
                     sx={{ p: 1.1, borderRadius: 2, bgcolor: 'rgba(0,229,160,.06)', border: '1px dashed rgba(0,229,160,.4)', cursor: 'pointer' }}>
                  <Typography sx={{ color: C.dim, fontSize: 11.5 }}>{k}</Typography>
                  <Typography sx={{ color: C.neon, fontSize: 14, fontWeight: 700, wordBreak: 'break-word' }}>{String(v)}</Typography>
                </Box>
              ) : (
                <Box key={k} sx={{ p: 1.1, borderRadius: 2, bgcolor: 'rgba(255,255,255,.03)' }}>
                  <Typography sx={{ color: C.dim, fontSize: 11.5 }}>{k}</Typography>
                  <Typography sx={{ color: C.txt, fontSize: 14, fontWeight: 600, wordBreak: 'break-word' }}>{String(v)}</Typography>
                </Box>
              )))}
            </Box>
            </>)}
            {view === 'money' && <MoneyHist mh={mh} />}
            {view === 'sim' && loadSim && <SimTab rows={sim} />}   {/* [D-701] лише дашборд OWNER */}
            {view === 'price' && (<>   {/* [D-619] своя ціна пакета для цього учня */}
              <Typography sx={{ color: C.dim, fontSize: 12 }}>
                Своя ціна діє на НАСТУПНІ оплати: бот і Хаб показують учню її, і оплата записується за нею. Порожнє поле - як у пакеті.
                Уже записану оплату виправляйте в «💶 Фінанси» → «💳 Оплати» → «✏️ сума».
              </Typography>
              {prices === null && <Typography sx={{ color: C.dim, fontSize: 13 }}>Завантаження…</Typography>}
              {prices && prices.length === 0 && <Typography sx={{ color: C.dim, fontSize: 13 }}>У партнера ще немає пакетів.</Typography>}
              <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
                {(prices || []).map(k => (
                  <Box key={k.package_id} sx={{ p: 1.3, borderRadius: 2, bgcolor: k.own != null ? 'rgba(255,183,77,.10)' : 'rgba(255,255,255,.03)',
                                                border: '1px solid ' + (k.own != null ? 'rgba(255,183,77,.45)' : 'transparent') }}>
                    <Typography sx={{ color: C.txt, fontWeight: 700, fontSize: 14 }}>📦 {k.name}{k.active ? '' : ' (не в продажу)'}</Typography>
                    <Typography sx={{ color: C.dim, fontSize: 12.5 }}>ціна пакета: {k.base} {k.ccy === 'EUR' ? '€' : '₴'}{k.own != null ? ' · для цього учня: ' + k.own + (k.ccy === 'EUR' ? ' €' : ' ₴') : ''}</Typography>
                    <Box sx={{ display: 'flex', gap: .8, alignItems: 'center', mt: .8 }}>
                      <TextField size="small" label="своя ціна / міс" key={'pr' + k.package_id + k.own} defaultValue={k.own == null ? '' : k.own} disabled={pBusy}
                        inputProps={{ style: { color: C.txt, width: 90 } }} InputLabelProps={{ style: { color: C.dim } }}
                        onBlur={e => {
                          const v = e.target.value.trim();
                          if ((v === '' && k.own == null) || (v !== '' && Number(v.replace(',', '.')) === k.own)) return;
                          setPBusy(true);
                          setPrice(userId, k.package_id, v === '' ? null : v).then(() => loadPrices(userId).then(setPrices)).then(() => setErr(''))
                            .catch(er => setErr('Не вдалося зберегти ціну' + ((er.response && er.response.data && er.response.data.detail) ? ': ' + er.response.data.detail : '')))
                            .finally(() => setPBusy(false));
                        }} />
                      {k.own != null && <Button size="small" disabled={pBusy} sx={{ textTransform: 'none', color: C.dim }}
                        onClick={() => { setPBusy(true); setPrice(userId, k.package_id, null).then(() => loadPrices(userId).then(setPrices)).finally(() => setPBusy(false)); }}>↩ як у пакеті</Button>}
                    </Box>
                  </Box>
                ))}
              </Box>
            </>)}
            {view === 'feats' && (<>
              <Typography sx={{ color: C.dim, fontSize: 11.5 }}>
                «Як усім» — діє те, що задано для партнера й для всіх. Особисте налаштування перебиває партнерське.
              </Typography>
              <FeatureLegend />
              {feats === null
                ? <Typography sx={{ color: C.dim, fontSize: 13 }}>Завантаження…</Typography>
                : <FeatureRows rows={feats} onSet={onSet} busyKey={busy} inheritLabel="Як у партнера" />}   {/* [D-468] */}
            </>)}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <span />{/* [D-728] «📄 Повна сторінка юзера» - тепер кнопкою «🎬 Обробки й уся інформація» угорі */}
        {onDelete && (   /* [D-460] повне видалення — щоб тим самим акаунтом знову пройти шлях учня */
          <Button onClick={() => onDelete(userId)} sx={{ color: '#ff5252', textTransform: 'none' }}>🗑 Видалити учня</Button>
        )}
        {onUnbind && (   /* [D-452] G13: тест на своєму акаунті / помилкова прив'язка */
          <Button onClick={() => onUnbind(userId)} sx={{ color: '#ff7a59', textTransform: 'none' }}>🔗 Відв'язати від партнера</Button>
        )}
        <Button onClick={onClose} sx={{ color: '#4fc3f7', textTransform: 'none' }}>Закрити</Button>
      </DialogActions>
    </Dialog>
  );
}
